import { ReactNode } from 'react';
import { cn } from '~/lib/cn';
import Tooltip from './Tooltip';

export interface Chip {
    key: string;
    label: string;
    /** Accessible name when the visible label is deliberately icon-only. */
    ariaLabel?: string;
    /** Optional trailing count, rendered muted next to the label. */
    count?: number;
    icon?: ReactNode;
    show?: boolean;
}

interface Props {
    chips: Chip[];
    /** The selected key. Ignored when `activeChips` is given. */
    activeChip?: string;
    /**
     * Several selected keys, for questions that have more than one answer.
     *
     * "Welche Gruppengrößen kommen infrage?" is answered with "2, 3 oder 4" as
     * often as with one number, and a single-select control would record a
     * preference the person never stated. Opt-in, so every existing group stays
     * exactly as strict as it was.
     */
    activeChips?: string[];
    onChange: (key: string) => void;
    className?: string;
    /** Renders the group on a muted track — use for segmented "one of N" switches. */
    segmented?: boolean;
    /**
     * Splits the width evenly between the chips instead of letting each take
     * what its label needs.
     *
     * For a segmented switch that spans a panel: two chips of different word
     * lengths sitting left in a full-width track read as one chip and a gap,
     * and the track stops looking like a choice between two things.
     */
    fill?: boolean;
    /**
     * The narrow-rail variant: tighter padding and a step down in label size.
     *
     * Four chips do not fit across a 272px sidebar at the full `px-3` and 12px,
     * and the two ways out are both worse than this one. Wrapping puts a second
     * row inside the grey track and costs the panel a line of height it never
     * gets back; shortening the words costs the reader the words. So the chips
     * give up a step of type instead — visibly smaller than a chip in a
     * full-width panel, which is the honest signal that this is a tighter place.
     *
     * Here rather than in the caller: a narrow rail that overrides the label
     * through `[&>button]` gets the same result, and the next narrow rail copies
     * the string rather than the reasoning.
     */
    dense?: boolean;
}

// Pill-style filter group, single-select by default. Sibling of InlineTabBar for cases where tabs would be too heavy.
const ChipGroup = ({ chips, activeChip, activeChips, onChange, className, segmented, fill, dense }: Props) => (
    <div
        className={cn(
            'tw-flex tw-items-center',
            // Filling and wrapping are the same question answered twice: chips
            // that share the width evenly have nowhere to wrap to.
            fill ? 'tw-w-full' : 'tw-flex-wrap',
            segmented ? 'tw-gap-0.5 tw-rounded-lg tw-bg-accents-1 tw-p-1' : 'tw-gap-2',
            className
        )}
    >
        {chips
            .filter((chip) => chip.show !== false)
            .map((chip) => {
                const active = activeChips ? activeChips.includes(chip.key) : chip.key === activeChip;
                const button = (
                    <button
                        key={chip.key}
                        type="button"
                        aria-pressed={active}
                        aria-label={chip.ariaLabel}
                        onClick={() => onChange(chip.key)}
                        className={cn(
                            'tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-border tw-border-solid tw-text-xs tw-font-semibold tw-transition-colors',
                            // `overflow-hidden` is the floor under `fill`: an even
                            // split plus `min-w-0` lets a chip be narrower than its own
                            // label, and with `whitespace-nowrap` the text then ran out
                            // across its neighbours instead of being clipped by them.
                            fill && 'tw-min-w-0 tw-flex-1 tw-justify-center tw-overflow-hidden',
                            segmented ? 'tw-rounded-md tw-py-1.5' : 'tw-rounded-full tw-py-1.5',
                            dense ? 'tw-px-1.5 tw-text-[11px]' : 'tw-px-3',
                            /*
                             * One selected look for both shapes, and it is the
                             * one the application uses everywhere else for
                             * "this is the one".
                             *
                             * The segmented variant used to be white on the
                             * `accents-1` track — 1.14:1, which is not a
                             * contrast, it is a rumour. The only thing actually
                             * separating the selected chip was a 1px inset
                             * shadow, and the only thing that reliably moved
                             * when you looked at it was the hover colour, so
                             * the control read as "nothing is selected until I
                             * point at it".
                             *
                             * Dark label rather than the green one the pill
                             * variant carried: `success` on `successLight` is
                             * 2.12:1 and fails at any size, while `accents-9`
                             * on it is 16:1. The green stays as the fill and
                             * the border, so the state is not carried by colour
                             * alone.
                             */
                            active
                                ? 'tw-border-success tw-bg-successLight tw-text-accents-9'
                                : cn(
                                      'tw-text-accents-8 hover:tw-text-accents-9',
                                      segmented
                                          ? 'tw-border-transparent tw-bg-transparent'
                                          : 'tw-border-accents-4 tw-bg-white hover:tw-border-accents-5'
                                  )
                        )}
                    >
                        {chip.icon}
                        {chip.label}
                        {undefined !== chip.count && (
                            <span className={cn('tw-font-normal', active ? 'tw-opacity-70' : 'tw-text-accents-6')}>
                                {chip.count}
                            </span>
                        )}
                    </button>
                );

                /*
                 * An icon-only chip says what it is through `ariaLabel`, and a
                 * pointer needs to be told the same thing. The native `title`
                 * this used to carry answered about a second late, in the
                 * system's font, somewhere else on screen — so a group of icon
                 * chips beside any control using the real tooltip read as two
                 * different products. `useFocus` inside Tooltip also puts the
                 * name within reach of the keyboard, which `title` never was.
                 */
                if (!chip.ariaLabel) {
                    return button;
                }

                return (
                    <Tooltip
                        key={chip.key}
                        flat
                        withArrow
                        placement="bottom"
                        tooltip={chip.ariaLabel}
                        className={cn('tw-inline-flex', fill && 'tw-min-w-0 tw-flex-1')}
                    >
                        {button}
                    </Tooltip>
                );
            })}
    </div>
);

export default ChipGroup;
